// =============================================================================
// Updated AladinViewer.jsx - Removed click handlers and yellow marker
import React, { useEffect, useRef } from 'react';

const ALADIN_BASE_URL = 'https://aladin.cds.unistra.fr/AladinLite/api/v3/latest';
const ALADIN_LOAD_TIMEOUT_MS = 8000;

const loadAladin = () => {
  if (window.A) return Promise.resolve();
  if (window.__aladinLoadPromise) return window.__aladinLoadPromise;

  window.__aladinLoadPromise = new Promise((resolve, reject) => {
    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = `${ALADIN_BASE_URL}/aladin.css`;
    document.head.appendChild(stylesheet);

    const script = document.createElement('script');
    script.src = `${ALADIN_BASE_URL}/aladin.js`;
    script.async = true;

    const timeout = window.setTimeout(() => {
      script.remove();
      reject(new Error('Timed out while loading Aladin Lite from CDS'));
    }, ALADIN_LOAD_TIMEOUT_MS);

    script.onload = () => {
      window.clearTimeout(timeout);
      window.A ? resolve() : reject(new Error('Aladin Lite loaded without its API'));
    };
    script.onerror = () => {
      window.clearTimeout(timeout);
      reject(new Error('Unable to load Aladin Lite from CDS'));
    };
    document.head.appendChild(script);
  }).catch((error) => {
    // Permit a later visit/retry to make a fresh request.
    window.__aladinLoadPromise = null;
    throw error;
  });

  return window.__aladinLoadPromise;
};

const AladinViewer = ({
  aladinInstance,
  isLoading,
  setIsLoading,
  setError,
  setIsAladinReady,
  showConeSearch, // Not used anymore but kept for compatibility
  setSelectedCoords, // Not used anymore but kept for compatibility
  setSelectedObject // Not used anymore but kept for compatibility
}) => {
  const aladinRef = useRef(null);

  // Initialize Aladin
  useEffect(() => {
    const initAladin = () => {
      if (!window.A || !aladinRef.current || aladinInstance.current) return;

      const makeInstance = () => {
        aladinInstance.current = window.A.aladin(aladinRef.current, {
          survey: 'P/DESI-Legacy-Surveys/DR10/color',
          fov: 1,
          projection: 'SIN',
          cooFrame: 'ICRSd',
          showCooGridControl: true,
          showSimbadPointerControl: true,
          showCooGrid: false,
          showFrame: true,
          showCatalog: true,
          target: '0.1 0.1',
          fullScreen: true,
          reticleColor: '#9c9687',
          reticleSize: 22,
        });

        setIsAladinReady(true);
        setIsLoading(false);
        // No longer attaching click handlers
      };

      try {
        if (window.A.init && typeof window.A.init.then === 'function') {
          window.A.init
            .then(() => makeInstance())
            .catch((e) => {
              console.error('Error initializing Aladin:', e);
              setError('Failed to initialize Aladin Lite');
              setIsLoading(false);
            });
        } else {
          makeInstance();
        }
      } catch (e) {
        console.error('Error with Aladin:', e);
        setError('Error loading Aladin Lite');
        setIsLoading(false);
      }
    };

    let cancelled = false;

    loadAladin()
      .then(() => {
        if (!cancelled && !aladinInstance.current) initAladin();
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Error loading Aladin Lite:', error);
          setError('Aladin Lite is currently unavailable. Please try again later.');
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      aladinInstance.current = null;
    };
  }, [setError, setIsLoading, setIsAladinReady]);

  return (
    <div
      ref={aladinRef}
      className="w-full h-full"
      style={{ minHeight: '100vh' }}
    />
  );
};

export default AladinViewer;
